import { isTaskType, layoutMap } from '@caliber-2/discovery-core/maps';
import type { MapStepDto, ProcessMapDto } from '@/lib/api/process-maps';
import { modelFromDto } from '../maps/map-logic';

/**
 * An area's process as a list (the Findings area page, 5 Nov 2026): the
 * steps that do work, in reading order, each with the confirmed quotes it
 * cites and the pain points that sit on it. Pure: every fact here is the
 * map read's own (the step's confirmation, its citations with their text,
 * its lane), so the list and the map can never disagree.
 *
 * Decisions and the start and end points are left out: only a step that
 * does work needs evidence (`TASK_TYPES`). Branches stay on the map.
 */

/** A confirmed quote a step cites, as the map read carries it. */
export interface StepQuote {
  evidence_id: string;
  number: string | null;
  text: string;
}

export interface StepRow {
  key: string;
  /** 1-based, in reading order, counting only the steps that do work. */
  no: number;
  name: string;
  lane_key: string;
  lane_name: string;
  system: string | null;
  /** Rests on a confirmed quote or the client's validation (`confirmation === 'confirmed'`). */
  described: boolean;
  /** What it cited was withdrawn (Decision 54.20). */
  needs_attention: boolean;
  /** The client validated a version with this step. */
  validated: boolean;
  origin: MapStepDto['origin'];
  origin_user: string | null;
  quotes: StepQuote[];
  pain_points: Array<{ id: string; number: string | null }>;
  /** The AI's pending question for a step nobody has described (T-0286). */
  gap_question: { ai_proposal_id: string; text: string; why: string } | null;
}

/**
 * The steps that do work, in the order the map lays them out (its columns,
 * then its lanes, then the model's own order), numbered from 1.
 */
export function stepRows(dto: ProcessMapDto): StepRow[] {
  const layout = layoutMap(modelFromDto(dto));
  const laneName = new Map(dto.lanes.map((l) => [l.key, l.name]));
  const index = new Map(dto.steps.map((s, i) => [s.key, i]));
  const at = (key: string) => layout.steps.get(key);
  const far = Number.MAX_SAFE_INTEGER;
  const tasks = dto.steps
    .filter((s) => isTaskType(s.type))
    .sort(
      (a, b) =>
        (at(a.key)?.column ?? far) - (at(b.key)?.column ?? far) ||
        (at(a.key)?.row ?? 0) - (at(b.key)?.row ?? 0) ||
        (index.get(a.key) ?? 0) - (index.get(b.key) ?? 0),
    );
  return tasks.map((s, i) => ({
    key: s.key,
    no: i + 1,
    name: s.name,
    lane_key: s.lane,
    lane_name: laneName.get(s.lane) ?? '',
    system: s.system,
    described: s.confirmation === 'confirmed',
    needs_attention: s.needs_attention,
    validated: s.citations.some((c) => c.kind === 'client_validation'),
    origin: s.origin,
    origin_user: s.origin_user?.name ?? null,
    quotes: s.citations.flatMap((c) =>
      c.kind === 'discovery_evidence' && c.evidence_id && c.evidence_status === 'confirmed'
        ? [{ evidence_id: c.evidence_id, number: c.evidence_number ?? null, text: c.text ?? '' }]
        : [],
    ),
    pain_points: s.pain_points.map((p) => ({ id: p.id, number: p.number })),
    gap_question: s.gap_question ?? null,
  }));
}

export interface LaneRun {
  lane_key: string;
  lane_name: string;
  rows: StepRow[];
  described: number;
}

/**
 * The rows grouped by who does the work, without reordering them: a new
 * group starts each time the lane changes, so a process that goes back to
 * the department lists the department twice, in order.
 */
export function laneRuns(rows: readonly StepRow[]): LaneRun[] {
  const runs: LaneRun[] = [];
  for (const row of rows) {
    const last = runs[runs.length - 1];
    if (last && last.lane_key === row.lane_key) {
      last.rows.push(row);
      if (row.described) last.described += 1;
    } else {
      runs.push({
        lane_key: row.lane_key,
        lane_name: row.lane_name,
        rows: [row],
        described: row.described ? 1 : 0,
      });
    }
  }
  return runs;
}

export function stepCoverage(rows: readonly StepRow[]): { total: number; described: number } {
  return { total: rows.length, described: rows.filter((r) => r.described).length };
}

/**
 * Why a step has no quote, in plain words, and where the step came from:
 * an AI draft keeps the steps of the firm's reference process that no
 * evidence describes; anything else was added on the map by a person.
 */
export function undescribedLine(
  row: Pick<StepRow, 'needs_attention' | 'origin' | 'origin_user'>,
): string {
  if (row.needs_attention) return 'The quote it cited was withdrawn. No confirmed quote is left.';
  if (row.origin === 'ai_draft') return 'No quote yet. Kept from the reference process.';
  return `No quote yet. Added on the map${row.origin_user ? ` by ${row.origin_user}` : ''}.`;
}

/** The step the detail opens on: the first described one, else the first. */
export function defaultStepKey(rows: readonly StepRow[]): string | null {
  return (rows.find((r) => r.described) ?? rows[0])?.key ?? null;
}

export interface QuoteSupport {
  /** The numbers of the steps that cite the quote. */
  steps: number[];
  /** The numbers of the confirmed pain points that rest on it. */
  pain_points: string[];
}

/** What each quote supports: `evidence id -> the steps and pain points that cite it`. */
export function quoteSupport(
  rows: readonly StepRow[],
  painPoints: ReadonlyArray<{
    number: string | null;
    status: string;
    evidence: ReadonlyArray<{ evidence_id: string }>;
  }>,
): Map<string, QuoteSupport> {
  const out = new Map<string, QuoteSupport>();
  const of = (id: string) => {
    let s = out.get(id);
    if (!s) {
      s = { steps: [], pain_points: [] };
      out.set(id, s);
    }
    return s;
  };
  for (const row of rows) for (const q of row.quotes) of(q.evidence_id).steps.push(row.no);
  for (const p of painPoints) {
    if (p.status !== 'confirmed' || !p.number) continue;
    for (const e of p.evidence) of(e.evidence_id).pain_points.push(p.number);
  }
  return out;
}
